import React, { useEffect } from "react";
import { BoxAd } from "@bloomberg/media-ui-ads";
import { useRegUIClient } from "@foundation/reg-ui-client";
import styles from "./watchlistLayout.module.css";
import CenterRightLayout from "~components/CenterRightLayout";
import {
  useError,
  usePortfolios,
  useUserPreferences,
  WatchlistContext,
  WatchlistProvider,
} from "~context/WatchlistContext";
import WatchlistTabs from "~components/Watchlist/delta/WatchlistTabs";
import WatchlistOverview from "~components/Watchlist/delta/WatchlistOverview";
import PositionsTable from "~components/Watchlist/delta/PositionsTable";
import ComparisonChartWrapper from "~components/Watchlist/delta/ComparisonChartWrapper";
import HeatmapWrapper from "~components/Watchlist/delta/HeatmapWrapper";
import SecurityTypeGrouping from "~components/Watchlist/delta/SecurityTypeGrouping";
import PortfolioLoadingState from "~components/Portfolio/PortfolioLoadingState";
import WatchlistNewsV2 from "~components/Watchlist/delta/News/WatchlistNewsV2";

/**
 * V2 Variant of the Watchlist Page Layout
 *
 * This is a barebone variant with the same layout structure but placeholder content.
 * Wire up your new data sources and state management here using WatchlistContext.
 */

// Initialize context with user data
function WatchlistInitializer() {
  const { session } = useRegUIClient();
  const store = React.useContext(WatchlistContext);

  useEffect(() => {
    if (session?.userInfo?.userId && store) {
      const { initialize } = store.getState();

      initialize(session.userInfo.userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.userInfo?.userId]);

  return null;
}

function WatchlistContentV2() {
  const preferences = useUserPreferences();
  const dashboardView = preferences?.dashboardView || "DASH";
  const showMaximizedComparison = dashboardView === "COMP";
  const showMaximizedHeatmap = dashboardView === "HTMP";
  const showOverviewDashboard = dashboardView === "DASH";

  return (
    <div>
      <WatchlistOverview />

      {/* Maximized Comparison Chart View */}
      {showMaximizedComparison && (
        <div className="px-[35px] py-6">
          <ComparisonChartWrapper />
        </div>
      )}

      {/* Maximized Heatmap View */}
      {showMaximizedHeatmap && (
        <div className="px-[35px] py-6">
          <HeatmapWrapper />
        </div>
      )}

      {/* Minimized Dashboard - Three-panel Overview */}
      {showOverviewDashboard && (
        <div className={styles.dashboardContainer}>
          {/* Panel 1: Comparison Chart (minimized) */}
          <div className={styles.flexiCompare}>
            <ComparisonChartWrapper />
          </div>

          {/* Separator */}
          <div className={styles.separator} role="separator" />

          {/* Panel 2: Heatmap */}
          <div className={styles.heatmap}>
            <HeatmapWrapper />
          </div>

          {/* Separator */}
          <div className={styles.separator} role="separator" />

          {/* Panel 3: Security Type Donut Chart */}
          <div className={styles.symbolType}>
            <SecurityTypeGrouping />
          </div>
        </div>
      )}

      <PositionsTable />
    </div>
  );
}

function WatchlistBoxAdV2() {
  return <BoxAd className={styles.ad} flexSizeAllowed />;
}

function WatchlistContent() {
  const { session } = useRegUIClient();
  const portfolios = usePortfolios();
  const error = useError();

  // Show error state if initialization failed
  // Prevents stuck loading when portfolios is empty due to error
  if (session?.isRegUIClientLoaded && error && portfolios.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Failed to load watchlist</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "10px",
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <>
      {session?.isRegUIClientLoaded ? (
        <>
          <WatchlistTabs />
          <WatchlistContentV2 />
        </>
      ) : (
        <PortfolioLoadingState />
      )}
      <CenterRightLayout
        contentWell={<WatchlistNewsV2 />}
        rightRail={<WatchlistBoxAdV2 />}
      />
    </>
  );
}

export default function WatchlistPageLayoutV2() {
  return (
    <WatchlistProvider>
      <WatchlistInitializer />
      <div className={styles.gridLayoutContainer}>
        <WatchlistContent />
      </div>
      {/* Plug-in client placeholder */}
      <div className="plug-banner" />
    </WatchlistProvider>
  );
}
